import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import { articleContents, type Block } from '@/mocks/articleContents';

function BlockRenderer({ block }: { block: Block }) {
  switch (block.kind) {
    case 'h2':
      return <h2 className="font-heading text-2xl font-semibold text-foreground-950 mt-8">{block.text}</h2>;
    case 'h3':
      return <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-6">{block.text}</h3>;
    case 'p':
      return <p className="text-foreground-700 leading-relaxed">{block.text}</p>;
    case 'list':
      return (
        <ul className="space-y-2">
          {block.items.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <i
                className={`${block.icon === 'bulb' ? 'ri-lightbulb-line text-accent-600' : 'ri-check-line text-primary-600'} w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0`}
              />
              <span className="text-sm">{item}</span>
            </li>
          ))}
        </ul>
      );
    case 'steps':
      return (
        <div className="space-y-4">
          {block.items.map((step, idx) => (
            <div key={idx} className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
                {idx + 1}
              </div>
              <div>
                <h4 className="font-semibold text-foreground-900">{step.title}</h4>
                <p className="text-sm text-foreground-600">{step.text}</p>
              </div>
            </div>
          ))}
        </div>
      );
    case 'table':
      return (
        <div className="overflow-x-auto my-4">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-background-300">
                {block.headers.map((header) => (
                  <th key={header} className="text-left py-2 px-3 text-sm font-semibold text-foreground-900">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, idx) => (
                <tr key={idx} className="border-b border-background-200/50">
                  {row.map((cell, cellIdx) => (
                    <td
                      key={cellIdx}
                      className={`py-2 px-3 text-sm ${cellIdx === 0 ? 'font-medium text-foreground-900' : 'text-foreground-600'}`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
}

export default function ArticlePage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.id === slug) || articles[0];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const blocks = articleContents[article.id] || [];

  const related = articles.filter((a) => a.id !== article.id).slice(0, 2);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <article className="w-full">
          <div className="relative h-[280px] md:h-[380px] overflow-hidden">
            <img
              src={article.image}
              alt={t(article.titleKey)}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-950/70 to-background-950/20" />
            <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 lg:px-8 pb-8">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 bg-primary-500 text-white text-xs font-medium rounded">
                    {article.category}
                  </span>
                  <span className="text-xs text-background-100/80">{article.readTime}</span>
                </div>
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                  {t(article.titleKey)}
                </h1>
              </div>
            </div>
          </div>

          <div className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14">
            <div className="max-w-3xl mx-auto">
              <div className="mb-8 p-4 bg-background-100 border border-background-200/50 rounded-lg">
                <p className="text-sm text-foreground-600">
                  <strong>Disclosure:</strong> This website is an independent informational resource and is not affiliated with CSSBuy. All links to our main site are clearly marked as external.
                </p>
              </div>

              <div className="space-y-6">
                {blocks.map((block, idx) => (
                  <BlockRenderer key={idx} block={block} />
                ))}
              </div>

              <div className="mt-12 pt-8 border-t border-background-200/60">
                <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-4">
                  {t('related_articles')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {related.map((item) => (
                    <Link
                      key={item.id}
                      to={`/article/${item.id}`}
                      className="group p-4 bg-white border border-background-200/60 rounded-lg hover:border-primary-300/50 transition-all"
                    >
                      <span className="text-xs text-primary-600 font-medium">{item.category}</span>
                      <p className="font-heading text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mt-1">
                        {t(item.titleKey)}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
                  {t('back_to_home')}
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}